import {
  action, computed, makeAutoObservable, observable, when,
} from 'mobx';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { EcommerceAnalytics } from '@gamiphy/loyalty-station-desktop/store/analytics';
import { CoreStore } from '@gamiphy/loyalty-station-desktop/store/core';
import { AnalyticsFactory } from '@gamiphy/analytics';
import { EventsStore } from '@gamiphy/loyalty-station-desktop/store/events';
import { ProductDto, ProductsApi } from '@coretava/api/ecommerce-service';
import { DisplayString, getDisplayStringValue } from '@coretava/i18n';
import { i18next } from '@gamiphy/react-i18n';
import { AppStore } from '../app';

class ProductStore {
  @observable initialized = false;

  @observable productsMap: {
    [key: string]: ProductDto | undefined
  } = {};

  productRequestQueue: string[] = [];

  productRequestQueueWorking = false;

  @observable products?: ProductDto[] | [] = undefined;

  @observable viewedProduct?: ProductDto = undefined;

  constructor() {
    makeAutoObservable(this);

    when(() => CoreStore.initialized, () => {
      setInterval(() => {
        void this.productRequestQueueHandler();
      }, 100);
    });
  }

  @computed
  get api(): ProductsApi {
    return new ProductsApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed get productsInitialized(): boolean {
    return this.products !== undefined;
  }

  @action
  async productRequestQueueHandler(): Promise<void> {
    if (!this.productRequestQueueWorking && this.productRequestQueue.length > 0) {
      this.productRequestQueueWorking = true;

      const productId = this.productRequestQueue.shift();

      if (productId && !this.isProductLoaded(productId)) {
        this.productsMap = {
          ...this.productsMap,
          [productId]: (await this.api.productsControllerGet(AppStore.app.id, productId)).data.product,
        };
      }

      this.productRequestQueueWorking = false;
    }
  }

  @action requestProduct(productId: string): void {
    if (!this.productsMap[productId]) {
      this.productRequestQueue.push(productId);
    }
  }

  @action
  async initProducts(): Promise<void> {
    if (!this.productsInitialized) {
      const response = await this.api.productsControllerList(AppStore.app.id);
      this.products = response.data.products;
    }
  }

  @action
  public async trackViewProduct(externalProductId: string): Promise<void> {
    // Get product object by external id
    const response = await this.api.productsControllerGetByExternalId(AppStore.app.id, externalProductId);
    this.viewedProduct = response.data.product;
    // Track product viewed event
    void EventsStore.createEvent({
      type: 'productViewed',
      product: {
        id: this.viewedProduct.id,
        externalId: this.viewedProduct.externalId,
        name: getDisplayStringValue(i18next, this.viewedProduct.name as DisplayString),
        price: this.viewedProduct.price,
        categories: this.viewedProduct.categories,
        globalCategories: this.viewedProduct.globalCategories ?? [],
        brand: this.viewedProduct.brand ?? undefined,
        globalBrand: this.viewedProduct.globalBrand ?? undefined,
        globalProduct: this.viewedProduct.globalProduct,
      },
    });
    // Add GA event
    EcommerceAnalytics.trackProductPageView(this.viewedProduct);
    // Collect product view hit
    void AnalyticsFactory.collectProductView(externalProductId);
  }

  getProduct(productId: string): ProductDto | undefined {
    return this.productsMap[productId];
  }

  isProductLoaded(productId: string): boolean {
    return !!this.getProduct(productId);
  }
}

export default new ProductStore();
